import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, X } from "lucide-react";
import { useLang } from "../i18n";
import { track } from "../data/products";
import { EASE, Micro } from "./Reveal";

export const openQuote = () => window.dispatchEvent(new CustomEvent("intext:quote"));

const inputCls =
  "w-full bg-ink border border-white/10 px-4 py-3.5 text-sm text-cream placeholder:text-cream/30 focus:border-gold/60 focus:outline-none transition-colors duration-300";

export default function QuoteModal() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle");
  const [form, setForm] = useState({ name: "", phone: "", interest: "", message: "", company: "" });
  const interests = t("quote.interests");

  useEffect(() => {
    const h = () => {
      setStatus("idle");
      setOpen(true);
    };
    window.addEventListener("intext:quote", h);
    return () => window.removeEventListener("intext:quote", h);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, interest: form.interest || interests[0] }),
      });
      if (!res.ok) throw new Error();
      setStatus("success");
      track("quote:submit");
    } catch {
      setStatus("error");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="quote-modal"
          className="fixed inset-0 z-[160] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <motion.div
            data-lenis-prevent
            className="relative w-full max-w-lg bg-coal border border-gold/25 p-7 md:p-10 max-h-[90vh] overflow-y-auto"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
          >
            <button
              data-testid="quote-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="absolute top-4 right-4 text-cream/50 hover:text-gold transition-colors p-1"
            >
              <X className="w-5 h-5" />
            </button>

            {status === "success" ? (
              <div data-testid="quote-success" className="text-center py-10">
                <div className="w-14 h-14 mx-auto border border-gold flex items-center justify-center">
                  <Check className="w-6 h-6 text-gold" />
                </div>
                <h3 className="font-display uppercase text-2xl md:text-3xl mt-7">{t("quote.title")}</h3>
                <p className="text-sm text-cream/60 mt-4 max-w-xs mx-auto">{t("quote.success")}</p>
                <button
                  data-testid="quote-done"
                  onClick={() => setOpen(false)}
                  className="mt-8 bg-gold text-ink px-8 py-3.5 text-[11px] font-bold tracking-[0.25em] uppercase hover:bg-goldbright transition-colors"
                >
                  OK
                </button>
              </div>
            ) : (
              <>
                <Micro className="text-gold/80">INT–EXT</Micro>
                <h3 className="font-display uppercase text-2xl md:text-4xl mt-3">{t("quote.title")}</h3>
                <p className="text-sm text-cream/55 mt-3">{t("quote.subtitle")}</p>

                <form onSubmit={submit} className="mt-8 space-y-5">
                  <input
                    type="text"
                    name="company"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="absolute -left-[9999px] w-px h-px opacity-0"
                  />
                  <div>
                    <label htmlFor="quote-name" className="block text-[10px] tracking-[0.25em] uppercase text-cream/45 mb-2">
                      {t("quote.name")} *
                    </label>
                    <input
                      id="quote-name"
                      data-testid="quote-name-input"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="quote-phone" className="block text-[10px] tracking-[0.25em] uppercase text-cream/45 mb-2">
                      {t("quote.phone")} *
                    </label>
                    <input
                      id="quote-phone"
                      data-testid="quote-phone-input"
                      required
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <span className="block text-[10px] tracking-[0.25em] uppercase text-cream/45 mb-2">
                      {t("quote.interest")}
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {interests.map((opt, i) => (
                        <button
                          type="button"
                          key={opt}
                          data-testid={`quote-interest-${i}`}
                          onClick={() => setForm({ ...form, interest: opt })}
                          className={`px-4 py-2.5 text-[10px] tracking-[0.2em] uppercase border transition-colors duration-300 ${
                            (form.interest || interests[0]) === opt
                              ? "border-gold bg-gold/10 text-gold"
                              : "border-white/15 text-cream/55 hover:border-gold/40"
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="quote-message" className="block text-[10px] tracking-[0.25em] uppercase text-cream/45 mb-2">
                      {t("quote.message")}
                    </label>
                    <textarea
                      id="quote-message"
                      data-testid="quote-message-input"
                      rows={3}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className={`${inputCls} resize-none`}
                    />
                  </div>

                  {status === "error" && (
                    <p data-testid="quote-error" className="text-xs text-red-400">
                      {t("quote.error")}
                    </p>
                  )}

                  <button
                    type="submit"
                    data-testid="quote-submit"
                    disabled={status === "sending"}
                    className="group w-full inline-flex items-center justify-center gap-3 bg-gold text-ink px-8 py-4 text-[11px] font-bold tracking-[0.25em] uppercase hover:bg-goldbright transition-colors duration-300 disabled:opacity-60"
                  >
                    {status === "sending" ? t("quote.sending") : t("quote.send")}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
